import Phaser from "phaser";
import { Away, EntityKind, badgeArtUnit, iconCell, resolveColour, type EntityName, type EntityState } from "odyssey-shared";
import { UI_ATLAS_KEY } from "../assets.ts";
import type { WorldUiModule } from "../hook.ts";
import { PIXEL_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { BADGE_BOX, PARTY_DOT, badgeParts, badgeWidthFor, flagFrame, inVisionCap, newBadgeParts, newPlateLayout, onScreen, pickPlate, plateLayout, plateWidth, type BadgeParts, type PlateHit, type PlateLayout } from "./layout.ts";
import { infamyTint, plateForm, plateText, type PlateForm } from "./look.ts";
import { awayMarkFrame, awayMarkShown, awayMarkTint, heldMarkLayout, idleLoopStep, idleMarkLayout, newIdleMarkLayout, type IdleMarkLayout } from "./idle-mark.ts";
import { NO_PARTY_PLACE, PARTY_MARK_FRAME, PARTY_RIM_FRAME, partyMarkTint } from "./party.ts";
import { NameplatePool } from "./pool.ts";
import { createBadgeObjects, hideBadge, paintBadge, type BadgeObjects } from "./badge-draw.ts";
import { badgeDrawnKey, badgeLook, type BadgeLook } from "../../badge/look.ts";
import { iconFrame, iconSmoothTexture, iconTexture } from "../../icons/atlas-texture.ts";
import { NO_ICONS, type IconSource } from "../../icons/page-icons.ts";
import { PLATE_LIFT, barPenaltyOf, barShown } from "../health-bars/bar.ts";
import { blinkingFlag } from "../../hud/flag-countdown.ts";
import type { ReducedMotion } from "../../reduced-motion.ts";
import type { NameplateReport, PlateReading } from "./report.ts";

/**
 * Nameplates on the canvas (P1.9-T16, P1.9-T24; ADR-061 decisions 1 to 3 and its amendments; ADR-065 and its
 * amendment "the family is one look away"; ADR-067): every player character drawn carries a plate (a creature's is
 * `creature-plates/`'s, and this surface takes no plate for one) in the form the
 * viewer's Nameplates choices give it (`look.ts`). By default another player's plate is its first name and the
 * player's own is not drawn; Full name draws the first name and the family name joined by one space, Flag only the
 * mark alone and Off nothing, and the player's own plate, when shown, takes other players' form. The name is one
 * `BitmapText` in the pixel face at 1x, tinted its Infamy tier's name colour (name colour belongs to Infamy), with
 * its 8 px flag mark, or the locked variant, 2 logical px to its left. The UI camera draws them, so a nameplate pixel
 * is a HUD pixel and the world zoom changes where a plate stands, never its size, and the UI layer lies over the
 * whole world, faded tall props included.
 *
 * Over a player's plate, while that player is idle or AFK and alive and the viewer's Show idle and AFK marks choice
 * holds, stands its idle and AFK mark (ADR-090 decision 8 and its amendment of 2026-10-03, `idle-mark.ts`): the z's, an
 * atlas image whose frame follows the loop's step, and for AFK the word AFK beside them, both held by the plate; a
 * mark is laid out only when its plate's anchor, the UI scale or its state moves. Another player's mark hides with its
 * plate. The viewer's own character carries its mark too, read from its own entity's away state in the view as any
 * other's is, and its mark does not need its plate: while the own plate does not draw, the mark stands alone at the
 * anchor the plate would take, 2 px over where the plate's top would be. A character a Game Master has frozen (the
 * away state `Held`) shows the hourglass in the same box, whatever the viewer's choice (ADR-056's amendment "a frozen
 * character is safe", decision 45).
 * A plate is four display objects, the name, the flag mark, the idle and AFK mark's z's and its word; the health bar
 * (`health-bars/`, P2.1-T4) is a fifth, one sprite whose frame is cut from one strip, and the Later Infamy mark the
 * sixth. While a character's bar shows, its
 * plate stands the bar's height and a pixel higher, so the bar lies under the name.
 * Plates come from a pool (`pool.ts`): taken when a character appears, kept while it stays, returned when it leaves
 * the client's view. A plate is hidden, and nothing about it is updated, while the viewer's choices draw none for
 * its character, while its character is concealed from the viewer and not targeted (behind a prop or under a roof the
 * viewer is not under, ADR-061's amendment for P1.9-T73), while its character stands outside the vision cap's rectangle around the local character, since
 * the map server's margin sends characters the player must not see named (ADR-061 decision 6), or while it lies off
 * the canvas. A hidden plate stays with its character, so a choice that hides plates and one that shows them again
 * make no plate. Its text changes only when the name or the form does and the text drawn differs, its tint only when
 * the tier does and its mark only when the mark it draws does. While a character's flag byte carries the switching
 * bit (ADR-076's amendment of 2026-09-28) its mark alternates on the low-health flash's beat between the mark it holds
 * and the mark the switch ends in, one of the two at any moment (`blinkingFlag`); a watcher does not know the switch's
 * seconds left, so it alternates for the whole bit and never flickers; under reduced motion (the game's setting, read
 * each frame) the mark it holds stays.
 * The characters are visited through a function made once,
 * the choices are read from the report as they stand, the projection and the layout write into points made once,
 * and a plate is laid out only when what it draws, its anchor or the UI scale moves, so a change of choice lays each
 * plate out at most once. A frame of steady play allocates nothing here but the projection's coordinates, which at a
 * UI scale above 1 are fractional and stored as heap numbers, about 17 bytes a drawn plate a frame (CLIENT.md 7.16).
 */

/**
 * The widest plate in logical pixels before what it draws is known, for deciding a plate is off the canvas without
 * laying it out: a locked mark, the gap and a name at both parts' caps in the widest glyphs.
 */
const WIDEST_PLATE = 12 + 2 + 195;

/**
 * The word an AFK mark writes beside its z's (ADR-090 decision 8), drawn in the text colour.
 */
const AFK_WORD = "AFK";

/**
 * The text colour as a tint, the AFK word's.
 */
const TEXT_TINT = Number.parseInt(resolveColour("text").slice(1), 16);

/**
 * The frame an idle mark's image is made with, hidden until a mark draws.
 */
const FIRST_IDLE_FRAME = "ui/world/idle-3_16";

/**
 * One plate: its name and its flag mark, its idle and AFK mark, and what they show and where, so a frame that
 * changes nothing touches nothing.
 */
interface Plate {
  readonly text: Phaser.GameObjects.BitmapText;
  readonly mark: Phaser.GameObjects.Image;
  readonly rim: Phaser.GameObjects.Image;
  readonly party: Phaser.GameObjects.Image;
  readonly idle: Phaser.GameObjects.Image;
  readonly word: Phaser.GameObjects.BitmapText;
  away: number;
  awayFrame: string | null;
  awayX: number;
  awayY: number;
  awayScale: number;
  name: EntityName | null;
  form: PlateForm;
  infamy: number;
  flag: number;
  flagWidth: number;
  nameWidth: number;
  partyPlace: number;
  partyRevision: number;
  partyWidth: number;
  badge: BadgeLook | null;
  badgeObjects: BadgeObjects | null;
  badgeDrawn: string;
  badgeWidth: number;
  anchorX: number;
  anchorY: number;
  scale: number;
  shown: boolean;
  hitLeft: number;
  hitTop: number;
  hitWidth: number;
  hitHeight: number;
}

/**
 * Hides a plate's objects, its idle and AFK mark included, if shown.
 *
 * @param plate The plate.
 */
function hide(plate: Plate): void {
  hideName(plate);
  hideIdle(plate);
}

/**
 * Hides a plate's name and flag mark, if shown, leaving its idle and AFK mark as it stands.
 *
 * @param plate The plate.
 */
function hideName(plate: Plate): void {
  if (plate.shown) {
    plate.text.setVisible(false);
    plate.mark.setVisible(false);
    plate.party.setVisible(false);
    plate.rim.setVisible(false);
    if (plate.badgeObjects !== null) {
      hideBadge(plate.badgeObjects);
      plate.badgeDrawn = "";
    }
    plate.shown = false;
  }
}

/**
 * Hides a plate's idle and AFK mark, if shown.
 *
 * @param plate The plate.
 */
function hideIdle(plate: Plate): void {
  if (plate.away !== Away.Active) {
    plate.idle.setVisible(false);
    plate.word.setVisible(false);
    plate.away = Away.Active;
    plate.awayFrame = null;
  }
}

/**
 * The nameplates surface.
 *
 * @param report The game report's nameplates entry: its switch, the viewer's Nameplates choices, and where the
 *   pool's counts and readings go.
 * @param motion The game's reduced motion, read each frame (`PAGE_MOTION`).
 * @param icons The page's icons, whose atlas a staff badge's art is drawn from; none draws the glyph.
 * @returns The surface's module, listed after the UI atlas and the pixel font.
 */
export function nameplates(report: NameplateReport, motion: ReducedMotion, icons: IconSource = NO_ICONS): WorldUiModule {
  return {
    id: "nameplates",
    /**
     * Builds the pool and the frame's visitor, and lists the plates for the report.
     *
     * @param context The UI layer, the projection and the characters the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      const plates = new NameplatePool<Plate>(() => {
        const text = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("nameplate")));
        text.setOrigin(0, 0).setTint(infamyTint(0)).setVisible(false);
        const mark = context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, flagFrame(0).frame));
        mark.setOrigin(0, 0).setVisible(false);
        const rim = context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, PARTY_RIM_FRAME));
        rim.setOrigin(0, 0).setVisible(false);
        const party = context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, PARTY_MARK_FRAME));
        party.setOrigin(0, 0).setVisible(false);
        const idle = context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, FIRST_IDLE_FRAME));
        idle.setOrigin(0, 0).setVisible(false);
        const word = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, AFK_WORD, pixelFontSize("nameplate")));
        word.setOrigin(0, 0).setTint(TEXT_TINT).setVisible(false);
        return { text, mark, rim, party, idle, word, away: Away.Active, awayFrame: null, awayX: Number.NaN, awayY: Number.NaN, awayScale: 0, name: null, form: "none", infamy: 0, flag: -1, flagWidth: 0, nameWidth: 0, partyPlace: NO_PARTY_PLACE, partyRevision: -1, partyWidth: 0, badge: null, badgeObjects: null, badgeDrawn: "", badgeWidth: 0, anchorX: Number.NaN, anchorY: Number.NaN, scale: 0, shown: false, hitLeft: 0, hitTop: 0, hitWidth: 0, hitHeight: 0 };
      });
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const layout: PlateLayout = newPlateLayout();
      const markLayout: IdleMarkLayout = newIdleMarkLayout();
      const parts: BadgeParts = newBadgeParts();
      let texture: Phaser.Textures.Texture | null = null;
      let smoothTexture: Phaser.Textures.Texture | null = null;

      /**
       * Hands the page's decoded atlas to the texture manager once it is ready, filtered nearest; until then, and
       * for an atlas the page did not decode itself, a badge draws its glyph.
       */
      const adopt = (): void => {
        texture ??= iconTexture(scene.textures, icons.getSnapshot().atlas);
      };
      const unsubscribe = icons.subscribe(adopt);
      adopt();

      /**
       * The icon atlas frame a badge's art draws, or null where none is drawn: no decoded atlas, or a pointer
       * without the key, which says so once. The art is drawn at every UI scale, from the linear texture where it is downscaled and the
       * nearest one where it is whole. A frame the texture already holds is answered by name, so the lookup allocates
       * only the first time a key is drawn.
       *
       * @param look The badge's look.
       * @param smooth Whether the art is drawn downscaled (`BadgeParts.smooth`).
       * @returns The frame's name, or null.
       */
      const badgeFrame = (look: BadgeLook, smooth: boolean): string | null => {
        const atlas = icons.getSnapshot().atlas;
        if (smooth) {
          smoothTexture ??= iconSmoothTexture(scene.textures, atlas);
        }
        const from = smooth ? smoothTexture : texture;
        if (from === null || atlas.status !== "ready") {
          return null;
        }
        const drawn = from.has(look.key) ? look.key : iconFrame(from, look.key, () => iconCell(atlas.manifest, look.key));
        if (drawn === null) {
          icons.log.once(`key ${look.key}`, `the icon pointer has no ${look.key} (badge ${look.badge}); no badge is drawn`);
        }
        return drawn;
      };
      const camera = context.worldCamera;
      let now = 0;
      let reduced = false;
      let frame = 0;
      let shown = 0;
      let idleShown = 0;
      let idleStep = 0;
      let ownHandle = -1;

      /**
       * Returns a plate its character has left: hidden, and holding nothing of it, so the next character that
       * takes it sets its own name, form and mark.
       *
       * @param plate The plate.
       */
      const release = (plate: Plate): void => {
        hide(plate);
        plate.name = null;
        plate.form = "none";
        plate.flag = -1;
        plate.partyPlace = NO_PARTY_PLACE;
        plate.partyRevision = -1;
        plate.partyWidth = 0;
        plate.badge = null;
        plate.anchorX = Number.NaN;
      };

      /**
       * Gives a plate the staff badge its character now wears, made on the first one a plate shows, and hides it for
       * none; the plate is laid out again on its next frame.
       *
       * @param plate The plate.
       * @param look The badge's look, or null for none.
       */
      const setBadge = (plate: Plate, look: BadgeLook | null): void => {
        plate.badge = look;
        plate.badgeDrawn = "";
        if (look === null) {
          plate.badgeWidth = 0;
        }
        plate.anchorX = Number.NaN;
        if (look === null) {
          if (plate.badgeObjects !== null) {
            hideBadge(plate.badgeObjects);
          }
        } else {
          plate.badgeObjects ??= createBadgeObjects(context);
        }
      };

      /**
       * Sets a plate's party mark for the roster place its character holds: drawn in that member's map colour when it
       * holds one, hidden when it holds none, and laid out again when the mark appears or goes.
       *
       * @param plate The plate, whose name is drawn.
       * @param place The roster place 0 to 5, or `NO_PARTY_PLACE`.
       * @param revision The roster's revision this was read at.
       */
      const drawPartyMark = (plate: Plate, place: number, revision: number): void => {
        plate.partyRevision = revision;
        if (place === plate.partyPlace) {
          return;
        }
        plate.partyPlace = place;
        plate.partyWidth = place === NO_PARTY_PLACE ? 0 : PARTY_DOT;
        if (place !== NO_PARTY_PLACE) {
          plate.party.setTint(partyMarkTint(place));
        }
        plate.party.setVisible(plate.shown && place !== NO_PARTY_PLACE);
        plate.rim.setVisible(plate.shown && place !== NO_PARTY_PLACE);
        plate.anchorX = Number.NaN;
      };

      /**
       * Brings one drawn character's plate up to date for the frame. The viewer's own character, idle or AFK, whose
       * plate does not draw (the own plate is off by default) still has its mark drawn, at the anchor the plate would
       * take, with the plate's own objects. No bar lifts that anchor, since the own health bar is off with the own
       * plate, and while its plate does not draw the mark is not tested against the canvas, since the camera follows
       * the character.
       *
       * @param handle The character's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param state Its state, with its name, its Infamy tier, its flag byte and its Health percentage: a character
       *   whose health bar shows (`health-bars/`) has its plate lifted over the bar.
       * @param head How far its art reaches above its feet, in world pixels.
       * @param hidden Whether its world UI hides (ADR-061's amendment for P1.9-T73): concealed from the viewer and not
       *   targeted; its plate is kept, hidden, so it shows the frame the character does.
       */
      const visit = (handle: number, x: number, y: number, state: EntityState, head: number, hidden: boolean): void => {
        if (state.kind === EntityKind.Creature || state.kind === EntityKind.Npc) {
          return;
        }
        const plate = plates.take(handle, frame);
        const own = handle === ownHandle;
        const form = plateForm(own, report);
        const away = state.away ?? Away.Active;
        const named = !hidden && form !== "none" && inVisionCap(x - local.x, y - local.y) && state.name.first !== "";
        if (!named && !awayMarkShown(away, state.anim, own, false, report.idleMarks)) {
          hide(plate);
          return;
        }
        const scale = context.scale.current;
        context.project(x, y - head, anchor);
        const anchorX = Math.round(anchor.x * scale);
        const anchorY = Math.round(anchor.y * scale) - (barShown(state.health, own, report, barPenaltyOf(state.buffs)) ? PLATE_LIFT * scale : 0);
        if (!named) {
          hideName(plate);
          drawIdle(plate, away, anchorX, anchorY, scale);
          return;
        }
        const known = plate.name === state.name && plate.form === form;
        const look = badgeLook(state.badge ?? 0);
        if (look !== plate.badge) {
          setBadge(plate, look);
        }
        const reach = ((known ? plateWidth(plate.flagWidth, plate.nameWidth, plate.partyWidth, plate.badgeWidth) : WIDEST_PLATE + 2 + PARTY_DOT + BADGE_BOX + 2) * scale) / 2;
        if (!onScreen(anchorX - reach, anchorY - 32 * scale, 2 * reach, 32 * scale, camera.width, camera.height)) {
          hide(plate);
          return;
        }
        if (!known) {
          plate.name = state.name;
          plate.form = form;
          const text = plateText(form, state.name);
          if (text !== plate.text.text) {
            plate.text.setText(text);
            plate.nameWidth = form === "flag" ? 0 : plate.text.width;
            plate.anchorX = Number.NaN;
            report.texts += 1;
          }
        }
        if (!known || plate.partyRevision !== report.party.revision) {
          drawPartyMark(plate, report.party.placeOf(state.name.first, state.name.family), report.party.revision);
        }
        if (plate.infamy !== state.infamy) {
          plate.text.setTint(infamyTint(state.infamy));
          plate.infamy = state.infamy;
        }
        const drawn = blinkingFlag(state.pvp, now, reduced);
        if (plate.flag !== drawn) {
          const mark = flagFrame(drawn);
          plate.mark.setFrame(mark.frame);
          plate.flag = drawn;
          plate.flagWidth = mark.width;
          plate.anchorX = Number.NaN;
          report.marks += 1;
        }
        const artFrame = plate.badge === null || plate.badgeObjects === null ? null : badgeFrame(plate.badge, !badgeArtUnit(BADGE_BOX, scale).whole);
        const drawnBadge = badgeDrawnKey(plate.badge, artFrame);
        const badgeWidth = plate.badge === null ? 0 : badgeWidthFor(artFrame !== null, scale);
        if (anchorX !== plate.anchorX || anchorY !== plate.anchorY || scale !== plate.scale || drawnBadge !== plate.badgeDrawn) {
          plateLayout(anchorX, anchorY, scale, plate.flagWidth, plate.nameWidth, layout, plate.partyWidth, badgeWidth);
          plate.badgeWidth = badgeWidth;
          if (plate.badge !== null && plate.badgeObjects !== null) {
            badgeParts(layout.badgeX, layout.centreY, scale, parts);
            paintBadge(plate.badgeObjects, parts, artFrame);
          }
          plate.badgeDrawn = drawnBadge;
          plate.party.setPosition(layout.partyX, layout.partyY);
          plate.rim.setPosition(layout.partyX, layout.partyY);
          plate.text.setPosition(layout.textX, layout.textY);
          plate.mark.setPosition(layout.flagX, layout.flagY);
          plate.anchorX = anchorX;
          plate.anchorY = anchorY;
          plate.scale = scale;
          plate.hitLeft = layout.deviceLeft;
          plate.hitTop = layout.deviceTop;
          plate.hitWidth = layout.deviceWidth;
          plate.hitHeight = layout.deviceHeight;
          report.layouts += 1;
        }
        if (!plate.shown) {
          plate.text.setVisible(true);
          plate.mark.setVisible(true);
          plate.party.setVisible(plate.partyWidth > 0);
          plate.rim.setVisible(plate.partyWidth > 0);
          plate.shown = true;
        }
        shown += 1;
        if (awayMarkShown(away, state.anim, own, true, report.idleMarks)) {
          drawIdle(plate, away, anchorX, anchorY, scale);
        } else {
          hideIdle(plate);
        }
      };

      /**
       * Draws a plate's idle and AFK mark (ADR-090 decision 8 and its amendment of 2026-10-03) for a character
       * `idleMarkShown` says draws one: its frame the loop's step, laid out again only when the plate's anchor, the UI
       * scale or the state moves.
       *
       * @param plate The character's plate, whose name and flag mark may be hidden.
       * @param away The character's away state: idle, AFK, or held, whose hourglass takes the same box, still and in
       *   the info colour (ADR-056's amendment "a frozen character is safe", decision 45).
       * @param anchorX The plate's anchor across, in device pixels.
       * @param anchorY The plate's anchor down, in device pixels.
       * @param scale The UI scale.
       */
      const drawIdle = (plate: Plate, away: number, anchorX: number, anchorY: number, scale: number): void => {
        const afk = away === Away.Afk;
        if (plate.away !== away) {
          plate.idle.setTint(awayMarkTint(away));
        }
        if (plate.away !== away || anchorX !== plate.awayX || anchorY !== plate.awayY || scale !== plate.awayScale) {
          if (away === Away.Held) {
            heldMarkLayout(anchorX, anchorY, scale, markLayout);
          } else {
            idleMarkLayout(anchorX, anchorY, scale, afk ? plate.word.width : 0, markLayout);
          }
          plate.idle.setPosition(markLayout.zX, markLayout.zY);
          plate.word.setPosition(markLayout.wordX, markLayout.wordY);
          plate.word.setVisible(afk);
          plate.awayX = anchorX;
          plate.awayY = anchorY;
          plate.awayScale = scale;
        }
        const markFrame = awayMarkFrame(away, idleStep);
        if (plate.away !== away || markFrame !== plate.awayFrame) {
          if (markFrame === null) {
            plate.idle.setVisible(false);
          } else {
            plate.idle.setFrame(markFrame).setVisible(true);
          }
          plate.awayFrame = markFrame;
        }
        plate.away = away;
        idleShown += 1;
      };

      /**
       * The other character whose drawn plate holds a point, for the player menu: the plate in front when plates
       * overlap (`pickPlate`). Made on request, never on a frame.
       *
       * @param x The point's x in device pixels.
       * @param y The point's y in device pixels.
       * @returns The character's handle, or -1.
       */
      report.plateAt = (x: number, y: number): number => {
        const hits: PlateHit[] = [];
        plates.forEachActive((plate, handle) => {
          if (plate.shown && handle !== ownHandle) {
            hits.push({ handle, left: plate.hitLeft, top: plate.hitTop, width: plate.hitWidth, height: plate.hitHeight, anchorY: plate.anchorY });
          }
        });
        return pickPlate(x, y, hits);
      };
      /**
       * Reads every plate held, for the report: made on request, never on a frame.
       *
       * @returns One reading a plate.
       */
      report.list = (): readonly PlateReading[] => {
        const readings: PlateReading[] = [];
        plates.forEachActive((plate, handle) => {
          readings.push({
            handle,
            text: plate.text.text,
            shown: plate.shown,
            mark: plate.mark.frame.name,
            tint: plate.text.tintTopLeft,
            textX: plate.text.x,
            textY: plate.text.y,
            markX: plate.mark.x,
            markY: plate.mark.y,
            scale: plate.scale,
            width: plateWidth(plate.flagWidth, plate.nameWidth, plate.partyWidth, plate.badgeWidth),
            badge: plate.badge === null ? 0 : plate.badge.badge,
            badgeDrawn: plate.badgeDrawn,
            objects: 6,
            idleMark: plate.away === Away.Active ? "" : (plate.awayFrame ?? "blank"),
            idleWord: plate.word.visible,
            idleX: plate.idle.x,
            idleY: plate.idle.y,
            party: plate.party.visible,
            partyX: plate.party.x,
            partyY: plate.party.y,
            partyTint: plate.party.tintTopLeft,
            inUiLayer: plate.text.displayList === context.layer && plate.party.displayList === context.layer && plate.rim.displayList === context.layer && plate.mark.displayList === context.layer && plate.idle.displayList === context.layer && plate.word.displayList === context.layer,
          });
        });
        return readings;
      };

      return {
        /**
         * Visits every character drawn this frame, then returns the plates of those that left. With the
         * switch off, or no local character drawn, nothing is visited and every plate goes back.
         *
         * @param time The scene's clock in ms, which times a switching mark's blink.
         */
        update(time) {
          frame += 1;
          now = time;
          reduced = motion.reduced();
          idleStep = idleLoopStep(time, reduced);
          shown = 0;
          idleShown = 0;
          if (report.enabled && context.characters.local(local)) {
            ownHandle = context.characters.localHandle();
            context.characters.forEach(visit);
          }
          plates.sweep(frame, release);
          report.active = plates.active;
          report.free = plates.free;
          report.created = plates.created;
          report.shown = shown;
          report.idleShown = idleShown;
        },
        /**
         * Forgets the report's list, which would hold the destroyed plates.
         */
        destroy() {
          unsubscribe();
          report.list = () => [];
          report.plateAt = () => -1;
        },
      };
    },
  };
}
